import { useQuery } from '@tanstack/react-query';
import {
  fetchActivityTesterPublicProfile,
  fetchActivityTesterPublicSchedule,
  fetchActivityTesterRecentJobs,
} from './api';

/**
 * Activity-side hooks powering the in-Activity tester profile dialog (#3967).
 *
 * Mirror the web `usePublicTesterProfile` / `usePublicTesterSchedule` /
 * `useTesterRecentJobs` hooks, but bind to the Discord-OAuth-guarded
 * Activity routes instead of the clerk-authed web routes. The underlying
 * handlers and response shapes are shared — only the auth wrapper differs.
 */

export function usePublicTesterProfile(
  accessToken: string | undefined,
  testerId: string | null,
) {
  return useQuery({
    queryKey: ['discord-activity-tester-profile', testerId],
    queryFn: () => fetchActivityTesterPublicProfile(accessToken!, testerId!),
    enabled: Boolean(accessToken) && Boolean(testerId),
    staleTime: 30_000,
    retry: 1,
  });
}

export function usePublicTesterSchedule(
  accessToken: string | undefined,
  testerId: string | null,
  startDate: string,
  endDate: string,
) {
  return useQuery({
    queryKey: ['discord-activity-tester-schedule', testerId, startDate, endDate],
    queryFn: () =>
      fetchActivityTesterPublicSchedule(accessToken!, testerId!, startDate, endDate),
    enabled: Boolean(accessToken) && Boolean(testerId),
    staleTime: 60_000,
    retry: 1,
  });
}

/**
 * Admin-only data source — the History tab in `<InternalTesterProfileDialog>`
 * never mounts unless `capabilities.isAdmin`, so the route is only ever hit
 * by admin viewers. The handler still server-side gates on admin-or-self,
 * so a non-admin who somehow reaches the URL gets 403.
 */
export function useTesterRecentJobs(
  accessToken: string | undefined,
  testerId: string | null,
) {
  return useQuery({
    queryKey: ['discord-activity-tester-recent-jobs', testerId],
    queryFn: () => fetchActivityTesterRecentJobs(accessToken!, testerId!),
    enabled: Boolean(accessToken) && Boolean(testerId),
    staleTime: 30_000,
    retry: 1,
  });
}
